import { useRef, useState } from "react";
import { ReactComponent as LiveRedDot } from "../../../assets/image/svg/live.svg";
import { getUomLabel } from "../../../components/dms_common/DmsUtils";
import { formatToThousands, StockSummaryData, styleUtils } from "./RealTimeStockUtils";
import "./StockSummaryTile.css";

const StockSummaryTile: React.FC<{ tileData: StockSummaryData }> = ({ tileData }) => {
  const { totalStockSummary, stockDetails } = tileData;
  const visibleStockSummary: Array<keyof typeof stockDetails> = [
    "availableQty",
    "committedQty",
    "onRouteQty",
    "unsellableQty",
    "inTransitQty",
  ];
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (containerRef.current) {
      setIsDragging(true);
      setStartX(e.pageX - containerRef.current.offsetLeft);
      setScrollLeft(containerRef.current.scrollLeft);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const { caseUnitLabel, otherUnitLabel, packUnitLabel } = getUomLabel();

  return (
    <div className="StockSummaryTile">
      <div className="stock-summary-tile-container"
        ref={containerRef}  
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div
          className={`total-stock-summary ${
            otherUnitLabel ? "total-stock-summary-increased-width" : ""
          }`}
        >
          <div className="total-stock-summary-heading">
            <span>Total Stock Summary</span>
            <LiveRedDot className="blink" style={{ width: "15px" }} />
          </div>
          <div className="card-details" style={{ padding: 0 }}>
            <div className="quantity-card-details">
              {caseUnitLabel && <QuantityTile
                label={caseUnitLabel}
                value={totalStockSummary?.totalCs}
                textColor="#227C9D"
              />}
              {otherUnitLabel && (
                <QuantityTile
                  label={otherUnitLabel}
                  value={totalStockSummary?.totalOther}
                  textColor="#227C9D"
                />
              )}
              {packUnitLabel && <QuantityTile
                label={packUnitLabel}
                value={totalStockSummary?.totalPc}
                textColor="#227C9D"
              />}
            </div>
            <div className="amount-card-details">
              <QuantityTile
                value={formatToThousands(totalStockSummary?.totalAmount)}
                isAmount={true}
                textColor="#227C9D"
              />
            </div>
          </div>
        </div>
        <div className="individual-stocks">
          {visibleStockSummary.map((key, idx) => {
            const { amount, cs, pc, other } = stockDetails[key];
            return (
              <div
                key={key}
                className={`individual-stock-card ${
                  otherUnitLabel ? "individual-stock-card-increased-width" : ""
                }`}
              >
                <p className="stock-category">{styleUtils[key].heading}</p>
                <div className="card-details" style={{ backgroundColor: styleUtils[key].bgColor }}>
                  <div className="quantity-card-details">
                    {caseUnitLabel && <QuantityTile label={caseUnitLabel} value={cs} />}
                    {otherUnitLabel && <QuantityTile label={otherUnitLabel} value={other} />}
                    {packUnitLabel && <QuantityTile label={packUnitLabel} value={pc} />}
                  </div>
                  <div className="amount-card-details">
                    <QuantityTile value={formatToThousands(amount)} isAmount={true} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const QuantityTile: React.FC<{
  label?: string;
  value?: string | number;
  isAmount?: boolean;
  textColor?: string;
}> = ({ label = "", value = "", isAmount = false, textColor = "#5c5c5c" }) => {
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  return (
    <div className="quantity-tile-container">
      <div className="quantity-text-container">
        <span style={{ color: textColor }}>{`${
          isAmount ? ConfigDictionary.currency : ""
        }${value}`}</span>
        {!isAmount && <span style={{ color: textColor }}>{label}</span>}
      </div>
    </div>
  );
};

export default StockSummaryTile;
